import { DocumentSchemaRecord } from '../../../services/documentSchemaService';
import { ValidationIssue, ValidationResult } from './types';
import {
    ACTION_FUNCTION_DEFINITIONS,
    validateActionFunctionParams,
} from '../../documents/config/actionFunctionDefinitions';

const VALID_KINDS = new Set(['section', 'input', 'action', 'view']);
const VALID_INPUT_TYPES = new Set([
    'text',
    'textarea',
    'richtext',
    'richtext-enhanced',
    'number',
    'date',
    'select',
    'radio',
    'tag-list',
    'search-text',
    'source-list',
    'repeater',
    'custom',
]);
const VALID_ACTION_TYPES = new Set(['button', 'checkbox', 'toggle-tags']);
const VALID_ACTION_FUNCTION_KEYS = new Set(
    Object.keys(ACTION_FUNCTION_DEFINITIONS)
);
const VALID_FUNCTION_KEYS = new Set([
    ...Array.from(VALID_ACTION_FUNCTION_KEYS),
    'implantConditionals',
    'appendImplantConditionBlock',
    'removeImplantConditionBlock',
    'cptCodes',
    'conditionals',
    'SignOffBackground',
    'resetDeviceDetails',
]);
const VALID_SEARCH_PROVIDER_KEYS = new Set(['implantSuggestions']);
const VALID_EXTENSIONS = new Set(['implantConditionsBlock']);
const VALID_DATA_SOURCES = new Set([
    'scanners',
    'policies',
    'previousExams',
    'previousTasks',
    'implantSuggestions',
    'previousPurposeOfImmobilization',
    'departmentUsers',
    'previousDeviceTypes',
    'previousPrepActions',
    'previousPrepMethods',
    'previousPrepVerifications',
]);
const VALID_STYLE_COLORS = new Set([
    'default',
    'secondary',
    'destructive',
    'outline',
    'ghost',
    'link',
    'success',
    'warning',
    'neutral',
]);
const VALID_STYLE_ICONS = new Set([
    'copy',
    'download',
    'save',
    'complete',
    'preview',
    'check',
    'check-circle',
    'warning',
    'alert',
]);
const VALID_NODE_STYLE_KEYS = new Set([
    'layout',
    'color',
    'icon',
    'size',
    'inline',
    'fullWidth',
    'noBorder',
    'noBackground',
    'className',
    'hideTitle',
    'hideLabel',
]);
const VALID_SCHEMA_STYLE_KEYS = new Set([
    'noBorder',
    'noBackground',
    'className',
]);

const IDENTIFIER_KEYWORDS = new Set([
    'true',
    'false',
    'null',
    'undefined',
    'if',
    'else',
    'return',
    'Math',
    'Date',
    'new',
]);

interface PendingRefCheck {
    type:
        | 'condition'
        | 'updateTarget'
        | 'updateSource'
        | 'importSourceAlias'
        | 'generateInstructionToken';
    path: string;
    value: string;
}

const createEmptyResult = (): ValidationResult => ({
    errors: [],
    warnings: [],
    isValidForSave: false,
});

const addIssue = (
    result: ValidationResult,
    severity: 'error' | 'warning',
    path: string,
    code: string,
    message: string
) => {
    const issue: ValidationIssue = { severity, path, code, message };
    if (severity === 'error') {
        result.errors.push(issue);
    } else {
        result.warnings.push(issue);
    }
};

const parseIdentifiers = (expr: string): string[] => {
    const strippedStrings = expr.replace(/'[^']*'|"[^"]*"/g, ' ');
    const tokens = strippedStrings.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) || [];
    return tokens.filter((token) => !IDENTIFIER_KEYWORDS.has(token));
};

const parseTemplateTokens = (template: string): string[] => {
    const tokenSet = new Set<string>();
    const templateTokenRegex = /{{\s*([A-Za-z0-9_.-]+)\s*}}/g;
    let match = templateTokenRegex.exec(template);

    while (match) {
        const token = match[1];
        if (token) {
            tokenSet.add(token);
        }
        match = templateTokenRegex.exec(template);
    }

    return Array.from(tokenSet);
};

const validateNodeStyle = ({
    node,
    path,
    result,
}: {
    node: any;
    path: string;
    result: ValidationResult;
}) => {
    if (node.layout !== undefined) {
        addIssue(
            result,
            'error',
            `${path}.layout`,
            'layout.legacy',
            'layout is no longer supported. Use style.layout instead'
        );
    }

    if (node.style === undefined) {
        return;
    }

    if (
        !node.style ||
        typeof node.style !== 'object' ||
        Array.isArray(node.style)
    ) {
        addIssue(
            result,
            'error',
            `${path}.style`,
            'style.invalid',
            'style must be an object when provided'
        );
        return;
    }

    const style = node.style;
    Object.keys(style).forEach((key) => {
        if (!VALID_NODE_STYLE_KEYS.has(key)) {
            addIssue(
                result,
                'warning',
                `${path}.style.${key}`,
                'style.key.unknown',
                `Unknown style key "${key}"`
            );
        }
    });

    if (style.layout !== undefined) {
        if (
            !style.layout ||
            typeof style.layout !== 'object' ||
            Array.isArray(style.layout)
        ) {
            addIssue(
                result,
                'error',
                `${path}.style.layout`,
                'style.layout.invalid',
                'style.layout must be an object'
            );
        } else {
            if (
                style.layout.width !== undefined &&
                typeof style.layout.width !== 'string'
            ) {
                addIssue(
                    result,
                    'error',
                    `${path}.style.layout.width`,
                    'style.layout.width',
                    'style.layout.width must be a string'
                );
            }
            if (
                style.layout.position !== undefined &&
                !['left', 'right', 'center'].includes(style.layout.position)
            ) {
                addIssue(
                    result,
                    'error',
                    `${path}.style.layout.position`,
                    'style.layout.position',
                    'style.layout.position must be "left", "right", or "center"'
                );
            }
        }
    }

    if (style.color !== undefined && typeof style.color !== 'string') {
        addIssue(
            result,
            'error',
            `${path}.style.color`,
            'style.color',
            'style.color must be a string'
        );
    } else if (
        typeof style.color === 'string' &&
        !VALID_STYLE_COLORS.has(style.color)
    ) {
        addIssue(
            result,
            'warning',
            `${path}.style.color`,
            'style.color.unknown',
            `Unknown style.color "${style.color}"`
        );
    }

    if (style.icon !== undefined && typeof style.icon !== 'string') {
        addIssue(
            result,
            'error',
            `${path}.style.icon`,
            'style.icon',
            'style.icon must be a string'
        );
    } else if (
        typeof style.icon === 'string' &&
        !VALID_STYLE_ICONS.has(style.icon)
    ) {
        addIssue(
            result,
            'warning',
            `${path}.style.icon`,
            'style.icon.unknown',
            `Unknown style.icon "${style.icon}"`
        );
    }

    if (
        style.size !== undefined &&
        !['sm', 'default', 'lg'].includes(style.size)
    ) {
        addIssue(
            result,
            'error',
            `${path}.style.size`,
            'style.size',
            'style.size must be "sm", "default", or "lg"'
        );
    }

    if (style.inline !== undefined && typeof style.inline !== 'boolean') {
        addIssue(
            result,
            'error',
            `${path}.style.inline`,
            'style.inline',
            'style.inline must be a boolean'
        );
    }

    if (style.fullWidth !== undefined && typeof style.fullWidth !== 'boolean') {
        addIssue(
            result,
            'error',
            `${path}.style.fullWidth`,
            'style.fullWidth',
            'style.fullWidth must be a boolean'
        );
    }

    if (style.noBorder !== undefined && typeof style.noBorder !== 'boolean') {
        addIssue(
            result,
            'error',
            `${path}.style.noBorder`,
            'style.noBorder',
            'style.noBorder must be a boolean'
        );
    }

    if (
        style.noBackground !== undefined &&
        typeof style.noBackground !== 'boolean'
    ) {
        addIssue(
            result,
            'error',
            `${path}.style.noBackground`,
            'style.noBackground',
            'style.noBackground must be a boolean'
        );
    }

    if (style.className !== undefined && typeof style.className !== 'string') {
        addIssue(
            result,
            'error',
            `${path}.style.className`,
            'style.className',
            'style.className must be a string'
        );
    }
};

export const validateSchemaRecord = (
    candidate: any,
    originalId: string | null
): ValidationResult => {
    const result = createEmptyResult();
    const pendingRefChecks: PendingRefCheck[] = [];
    const fieldIds = new Set<string>();
    const contextImportIds = new Set<string>();

    if (!candidate || typeof candidate !== 'object') {
        addIssue(
            result,
            'error',
            '$',
            'record.invalid',
            'Schema record must be a JSON object'
        );
        return result;
    }

    const requiredTopLevel = ['ownerType', 'key', 'name', 'schema', 'version'];
    requiredTopLevel.forEach((key) => {
        if (candidate[key] === undefined || candidate[key] === null) {
            addIssue(
                result,
                'error',
                `$.${key}`,
                'record.required',
                `Missing required field: ${key}`
            );
        }
    });

    if (originalId && candidate._id && candidate._id !== originalId) {
        addIssue(
            result,
            'error',
            '$._id',
            'record.immutable',
            'Field "_id" is immutable and cannot be changed'
        );
    }

    if (
        candidate.ownerType &&
        !['nordinsight', 'department'].includes(candidate.ownerType)
    ) {
        addIssue(
            result,
            'error',
            '$.ownerType',
            'record.ownerType',
            'ownerType must be "nordinsight" or "department"'
        );
    }

    if (candidate.ownerType === 'department' && !candidate.department_id) {
        addIssue(
            result,
            'error',
            '$.department_id',
            'record.departmentRequired',
            'department_id is required when ownerType is "department"'
        );
    }

    if (
        candidate.version !== undefined &&
        (!Number.isInteger(candidate.version) || candidate.version <= 0)
    ) {
        addIssue(
            result,
            'error',
            '$.version',
            'record.version',
            'version must be a positive integer'
        );
    }

    const schema = candidate.schema;
    if (!schema || typeof schema !== 'object') {
        addIssue(
            result,
            'error',
            '$.schema',
            'schema.invalid',
            'schema must be an object'
        );
        result.isValidForSave = false;
        return result;
    }

    ['id', 'sections'].forEach((key) => {
        if (schema[key] === undefined || schema[key] === null) {
            addIssue(
                result,
                'error',
                `$.schema.${key}`,
                'schema.required',
                `schema.${key} is required`
            );
        }
    });

    ['title', 'description'].forEach((key) => {
        if (schema[key] === undefined || schema[key] === null) {
            addIssue(
                result,
                'warning',
                `$.schema.${key}`,
                'schema.recommended',
                `schema.${key} is recommended`
            );
        }
    });

    if (!Array.isArray(schema.sections) || schema.sections.length === 0) {
        addIssue(
            result,
            'error',
            '$.schema.sections',
            'schema.sections',
            'schema.sections must be a non-empty array'
        );
    }

    if (schema.style !== undefined) {
        if (
            !schema.style ||
            typeof schema.style !== 'object' ||
            Array.isArray(schema.style)
        ) {
            addIssue(
                result,
                'error',
                '$.schema.style',
                'schema.style.invalid',
                'schema.style must be an object when provided'
            );
        } else {
            Object.keys(schema.style).forEach((key) => {
                if (!VALID_SCHEMA_STYLE_KEYS.has(key)) {
                    addIssue(
                        result,
                        'warning',
                        `$.schema.style.${key}`,
                        'schema.style.key.unknown',
                        `Unknown schema style key "${key}"`
                    );
                }
            });

            if (
                schema.style.noBorder !== undefined &&
                typeof schema.style.noBorder !== 'boolean'
            ) {
                addIssue(
                    result,
                    'error',
                    '$.schema.style.noBorder',
                    'schema.style.noBorder',
                    'schema.style.noBorder must be a boolean'
                );
            }

            if (
                schema.style.noBackground !== undefined &&
                typeof schema.style.noBackground !== 'boolean'
            ) {
                addIssue(
                    result,
                    'error',
                    '$.schema.style.noBackground',
                    'schema.style.noBackground',
                    'schema.style.noBackground must be a boolean'
                );
            }

            if (
                schema.style.className !== undefined &&
                typeof schema.style.className !== 'string'
            ) {
                addIssue(
                    result,
                    'error',
                    '$.schema.style.className',
                    'schema.style.className',
                    'schema.style.className must be a string'
                );
            }
        }
    }

    if (
        schema.workspace?.visibilityWhen !== undefined &&
        typeof schema.workspace.visibilityWhen !== 'string'
    ) {
        addIssue(
            result,
            'error',
            '$.schema.workspace.visibilityWhen',
            'workspace.visibilityWhen',
            'workspace.visibilityWhen must be a string expression'
        );
    }
    if (typeof schema.workspace?.visibilityWhen === 'string') {
        pendingRefChecks.push({
            type: 'condition',
            path: '$.schema.workspace.visibilityWhen',
            value: schema.workspace.visibilityWhen,
        });
    }

    if (
        schema.contextImports !== undefined &&
        !Array.isArray(schema.contextImports)
    ) {
        addIssue(
            result,
            'error',
            '$.schema.contextImports',
            'schema.contextImports',
            'schema.contextImports must be an array when provided'
        );
    }
    if (Array.isArray(schema.contextImports)) {
        schema.contextImports.forEach((rule: any, index: number) => {
            const rulePath = `$.schema.contextImports[${index}]`;

            if (!rule || typeof rule !== 'object') {
                addIssue(
                    result,
                    'error',
                    rulePath,
                    'contextImport.invalid',
                    'Each context import rule must be an object'
                );
                return;
            }

            if (!rule.id || typeof rule.id !== 'string') {
                addIssue(
                    result,
                    'error',
                    `${rulePath}.id`,
                    'contextImport.id',
                    'context import id is required and must be a string'
                );
            } else if (contextImportIds.has(rule.id)) {
                addIssue(
                    result,
                    'error',
                    `${rulePath}.id`,
                    'contextImport.duplicateId',
                    `Duplicate context import id "${rule.id}" found`
                );
            } else {
                contextImportIds.add(rule.id);
            }

            if (
                !rule.sourceSchemaKey ||
                typeof rule.sourceSchemaKey !== 'string'
            ) {
                addIssue(
                    result,
                    'error',
                    `${rulePath}.sourceSchemaKey`,
                    'contextImport.sourceSchemaKey',
                    'sourceSchemaKey is required and must be a string'
                );
            }

            if (!rule.sourceField || typeof rule.sourceField !== 'string') {
                addIssue(
                    result,
                    'error',
                    `${rulePath}.sourceField`,
                    'contextImport.sourceField',
                    'sourceField is required and must be a string'
                );
            }
        });
    }

    const visitNode = (node: any, path: string) => {
        if (!node || typeof node !== 'object') {
            addIssue(
                result,
                'error',
                path,
                'node.invalid',
                'Node must be an object'
            );
            return;
        }

        if (!node.id) {
            addIssue(
                result,
                'error',
                `${path}.id`,
                'node.id',
                'Node id is required'
            );
        } else if (fieldIds.has(node.id)) {
            addIssue(
                result,
                'error',
                `${path}.id`,
                'node.duplicateId',
                `Duplicate node id "${node.id}" found`
            );
        } else {
            fieldIds.add(node.id);
        }

        if (!node.title) {
            addIssue(
                result,
                'warning',
                `${path}.title`,
                'node.title',
                'Node title is recommended'
            );
        }

        if (!node.kind || !VALID_KINDS.has(node.kind)) {
            addIssue(
                result,
                'error',
                `${path}.kind`,
                'node.kind',
                'Node kind must be one of: section, input, action, view'
            );
        }

        validateNodeStyle({ node, path, result });

        if (node.hidden && typeof node.hidden === 'string') {
            pendingRefChecks.push({
                type: 'condition',
                path: `${path}.hidden`,
                value: node.hidden,
            });
        }
        if (node.disableWhen && typeof node.disableWhen === 'string') {
            pendingRefChecks.push({
                type: 'condition',
                path: `${path}.disableWhen`,
                value: node.disableWhen,
            });
        }
        if (node.functionKey && !VALID_FUNCTION_KEYS.has(node.functionKey)) {
            addIssue(
                result,
                'warning',
                `${path}.functionKey`,
                'function.unknown',
                `Unknown functionKey "${node.functionKey}"`
            );
        }

        if (Array.isArray(node.updates)) {
            node.updates.forEach((update: any, index: number) => {
                const updatePath = `${path}.updates[${index}]`;
                if (typeof update.target === 'string') {
                    pendingRefChecks.push({
                        type: 'updateTarget',
                        path: `${updatePath}.target`,
                        value: update.target,
                    });
                }
                if (typeof update.source === 'string') {
                    pendingRefChecks.push({
                        type: 'updateSource',
                        path: `${updatePath}.source`,
                        value: update.source,
                    });
                }
                if (typeof update.condition === 'string') {
                    pendingRefChecks.push({
                        type: 'condition',
                        path: `${updatePath}.condition`,
                        value: update.condition,
                    });
                }
            });
        }

        if (node.kind === 'input') {
            if (!node.inputType || !VALID_INPUT_TYPES.has(node.inputType)) {
                addIssue(
                    result,
                    'error',
                    `${path}.inputType`,
                    'input.inputType',
                    'Invalid inputType'
                );
            }
            if (node.searchProviderKey) {
                if (!VALID_SEARCH_PROVIDER_KEYS.has(node.searchProviderKey)) {
                    addIssue(
                        result,
                        'warning',
                        `${path}.searchProviderKey`,
                        'searchProvider.unknown',
                        `Unknown searchProviderKey "${node.searchProviderKey}"`
                    );
                }
            }
            if (Array.isArray(node.extensions)) {
                node.extensions.forEach((extension: string, index: number) => {
                    if (!VALID_EXTENSIONS.has(extension)) {
                        addIssue(
                            result,
                            'warning',
                            `${path}.extensions[${index}]`,
                            'extension.unknown',
                            `Unknown rich text extension "${extension}"`
                        );
                    }
                });
            }
            const source = node.data?.options?.source;
            if (source && !VALID_DATA_SOURCES.has(source)) {
                addIssue(
                    result,
                    'warning',
                    `${path}.data.options.source`,
                    'source.unknown',
                    `Unknown data source "${source}"`
                );
            }
            if (node.data?.repeaterUi !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.data.repeaterUi`,
                    'input.repeaterUi.legacy',
                    'data.repeaterUi is no longer supported. Use repeater instead'
                );
            }
            if (node.repeater !== undefined) {
                if (
                    !node.repeater ||
                    typeof node.repeater !== 'object' ||
                    Array.isArray(node.repeater)
                ) {
                    addIssue(
                        result,
                        'error',
                        `${path}.repeater`,
                        'input.repeater.invalid',
                        'repeater must be an object when provided'
                    );
                } else {
                    if (
                        !node.repeater.mode ||
                        ![
                            'manual',
                            'checklist',
                            'chip-add',
                            'combo-add',
                        ].includes(node.repeater.mode)
                    ) {
                        addIssue(
                            result,
                            'error',
                            `${path}.repeater.mode`,
                            'input.repeater.mode',
                            'repeater.mode must be "manual", "checklist", or "chip-add"'
                        );
                    }

                    [
                        'optionFieldId',
                        'detailFieldId',
                        'customValueFieldId',
                        'dedupeByFieldId',
                    ].forEach((key) => {
                        if (
                            node.repeater[key] !== undefined &&
                            typeof node.repeater[key] !== 'string'
                        ) {
                            addIssue(
                                result,
                                'error',
                                `${path}.repeater.${key}`,
                                `input.repeater.${key}`,
                                `repeater.${key} must be a string`
                            );
                        }
                    });

                    if (
                        node.repeater.allowCustomValues !== undefined &&
                        typeof node.repeater.allowCustomValues !== 'boolean'
                    ) {
                        addIssue(
                            result,
                            'error',
                            `${path}.repeater.allowCustomValues`,
                            'input.repeater.allowCustomValues',
                            'repeater.allowCustomValues must be a boolean'
                        );
                    }
                }
            }
            if (node.repeater !== undefined && node.inputType !== 'repeater') {
                addIssue(
                    result,
                    'error',
                    `${path}.repeater`,
                    'input.repeater.nonRepeater',
                    'repeater is only allowed when inputType is "repeater"'
                );
            }
            if (Array.isArray(node.addOns)) {
                node.addOns.forEach((addOn: any, index: number) => {
                    visitNode(addOn, `${path}.addOns[${index}]`);
                });
            }
            if (Array.isArray(node.fields)) {
                node.fields.forEach((child: any, index: number) => {
                    visitNode(child, `${path}.fields[${index}]`);
                });
            }
        }

        if (node.kind === 'action') {
            if (!node.actionType || !VALID_ACTION_TYPES.has(node.actionType)) {
                addIssue(
                    result,
                    'error',
                    `${path}.actionType`,
                    'action.actionType',
                    'Invalid actionType'
                );
            }
            if (node.buttonStyle !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.buttonStyle`,
                    'action.buttonStyle.legacy',
                    'buttonStyle is no longer supported. Use style.color/style.icon'
                );
            }
            if (node.variant !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.variant`,
                    'action.variant.legacy',
                    'variant is no longer supported. Use style.color'
                );
            }
            if (node.icon !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.icon`,
                    'action.icon.legacy',
                    'icon is no longer supported. Use style.icon'
                );
            }
            if (node.ui !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.ui`,
                    'action.ui.legacy',
                    'ui is no longer supported. Use style.inline/style.fullWidth/style.size'
                );
            }
            if (node.importConfig !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.importConfig`,
                    'action.importConfig.legacy',
                    'importConfig is no longer supported. Use functionParams'
                );
            }
            if (node.generateConfig !== undefined) {
                addIssue(
                    result,
                    'error',
                    `${path}.generateConfig`,
                    'action.generateConfig.legacy',
                    'generateConfig is no longer supported. Use functionParams'
                );
            }

            if (node.functionParams !== undefined && !node.functionKey) {
                addIssue(
                    result,
                    'error',
                    `${path}.functionParams`,
                    'action.functionParams.functionKey',
                    'functionParams requires functionKey'
                );
            }

            const functionParamsIssues = validateActionFunctionParams({
                functionKey: node.functionKey,
                functionParams: node.functionParams,
                path: `${path}.functionParams`,
            });
            functionParamsIssues.forEach((issue) =>
                addIssue(
                    result,
                    'error',
                    issue.path,
                    'action.functionParams',
                    issue.message
                )
            );

            if (
                node.functionParams !== undefined &&
                node.functionKey &&
                !VALID_ACTION_FUNCTION_KEYS.has(node.functionKey)
            ) {
                addIssue(
                    result,
                    'warning',
                    `${path}.functionParams`,
                    'action.functionParams.unvalidated',
                    `functionParams provided for unknown action functionKey "${node.functionKey}"`
                );
            }

            const functionParams =
                node.functionParams &&
                typeof node.functionParams === 'object' &&
                !Array.isArray(node.functionParams)
                    ? node.functionParams
                    : undefined;

            if (node.functionKey === 'importFromSchema' && functionParams) {
                if (typeof functionParams.sourceAlias === 'string') {
                    pendingRefChecks.push({
                        type: 'importSourceAlias',
                        path: `${path}.functionParams.sourceAlias`,
                        value: functionParams.sourceAlias,
                    });
                }
                if (typeof functionParams.targetField === 'string') {
                    pendingRefChecks.push({
                        type: 'updateTarget',
                        path: `${path}.functionParams.targetField`,
                        value: functionParams.targetField,
                    });
                }
                if (Array.isArray(functionParams.mappings)) {
                    functionParams.mappings.forEach(
                        (mapping: any, mappingIndex: number) => {
                            const mappingPath = `${path}.functionParams.mappings[${mappingIndex}]`;
                            if (typeof mapping?.sourceAlias === 'string') {
                                pendingRefChecks.push({
                                    type: 'importSourceAlias',
                                    path: `${mappingPath}.sourceAlias`,
                                    value: mapping.sourceAlias,
                                });
                            }
                            if (typeof mapping?.targetField === 'string') {
                                pendingRefChecks.push({
                                    type: 'updateTarget',
                                    path: `${mappingPath}.targetField`,
                                    value: mapping.targetField,
                                });
                            }
                        }
                    );
                }
                if (typeof functionParams.condition === 'string') {
                    pendingRefChecks.push({
                        type: 'condition',
                        path: `${path}.functionParams.condition`,
                        value: functionParams.condition,
                    });
                }
            }

            if (node.functionKey === 'generateReportField' && functionParams) {
                if (typeof functionParams.instruction === 'string') {
                    parseTemplateTokens(functionParams.instruction).forEach(
                        (token) => {
                            pendingRefChecks.push({
                                type: 'generateInstructionToken',
                                path: `${path}.functionParams.instruction`,
                                value: token,
                            });
                        }
                    );
                }
                if (typeof functionParams.targetField === 'string') {
                    pendingRefChecks.push({
                        type: 'updateTarget',
                        path: `${path}.functionParams.targetField`,
                        value: functionParams.targetField,
                    });
                }
                if (Array.isArray(functionParams.mappings)) {
                    functionParams.mappings.forEach(
                        (mapping: any, mappingIndex: number) => {
                            const mappingPath = `${path}.functionParams.mappings[${mappingIndex}]`;
                            if (typeof mapping?.sourceAlias === 'string') {
                                pendingRefChecks.push({
                                    type: 'importSourceAlias',
                                    path: `${mappingPath}.sourceAlias`,
                                    value: mapping.sourceAlias,
                                });
                            }
                            if (typeof mapping?.targetField === 'string') {
                                pendingRefChecks.push({
                                    type: 'updateTarget',
                                    path: `${mappingPath}.targetField`,
                                    value: mapping.targetField,
                                });
                            }
                        }
                    );
                }
                if (typeof functionParams.condition === 'string') {
                    pendingRefChecks.push({
                        type: 'condition',
                        path: `${path}.functionParams.condition`,
                        value: functionParams.condition,
                    });
                }
            }
        }

        if (node.kind === 'view') {
            if (
                node.viewType === 'requirements-status' &&
                node.viewProps !== undefined &&
                (typeof node.viewProps !== 'object' ||
                    Array.isArray(node.viewProps))
            ) {
                addIssue(
                    result,
                    'error',
                    `${path}.viewProps`,
                    'view.viewProps',
                    'viewProps must be an object when provided'
                );
            }

            if (node.viewType === 'requirements-status') {
                const dataSource = node.viewProps?.dataSource;
                if (!dataSource || typeof dataSource !== 'string') {
                    addIssue(
                        result,
                        'error',
                        `${path}.viewProps.dataSource`,
                        'view.requirementsStatus.dataSource',
                        'requirements-status view requires viewProps.dataSource'
                    );
                }
                if (node.viewProps?.requirements !== undefined) {
                    addIssue(
                        result,
                        'error',
                        `${path}.viewProps.requirements`,
                        'view.requirementsStatus.legacyRequirements',
                        'viewProps.requirements is no longer supported. Use a context data source payload'
                    );
                }
                if (node.viewProps?.variant !== undefined) {
                    addIssue(
                        result,
                        'error',
                        `${path}.viewProps.variant`,
                        'view.requirementsStatus.legacyVariant',
                        'viewProps.variant is no longer supported'
                    );
                }
            }
        }

        if (node.kind === 'section') {
            if (!node.sectionType) {
                addIssue(
                    result,
                    'error',
                    `${path}.sectionType`,
                    'section.sectionType',
                    'sectionType is required for section nodes'
                );
            }
            if (!Array.isArray(node.fields)) {
                addIssue(
                    result,
                    'error',
                    `${path}.fields`,
                    'section.fields',
                    'Section fields must be an array'
                );
            } else {
                node.fields.forEach((child: any, index: number) => {
                    visitNode(child, `${path}.fields[${index}]`);
                });
            }
            if (node.background?.condition) {
                pendingRefChecks.push({
                    type: 'condition',
                    path: `${path}.background.condition`,
                    value: node.background.condition,
                });
            }
            const backgroundKey =
                node.background?.functionKey || node.background?.component;
            if (backgroundKey && !VALID_FUNCTION_KEYS.has(backgroundKey)) {
                addIssue(
                    result,
                    'warning',
                    `${path}.background`,
                    'background.unknown',
                    `Unknown background handler "${backgroundKey}"`
                );
            }
        }
    };

    if (Array.isArray(schema.sections)) {
        schema.sections.forEach((section: any, index: number) => {
            visitNode(section, `$.schema.sections[${index}]`);
        });
    }

    pendingRefChecks.forEach((check) => {
        if (check.type === 'importSourceAlias') {
            if (!contextImportIds.has(check.value)) {
                addIssue(
                    result,
                    'warning',
                    check.path,
                    'ref.unknownContextAlias',
                    `Referenced context import alias "${check.value}" was not found`
                );
            }
            return;
        }

        if (check.type === 'updateTarget' || check.type === 'updateSource') {
            if (!fieldIds.has(check.value)) {
                addIssue(
                    result,
                    'warning',
                    check.path,
                    'ref.unknown',
                    `Referenced field "${check.value}" was not found`
                );
            }
            return;
        }

        if (check.type === 'generateInstructionToken') {
            const knownIdentifiers = new Set([
                ...Array.from(fieldIds),
                ...Array.from(contextImportIds),
            ]);

            if (!knownIdentifiers.has(check.value)) {
                addIssue(
                    result,
                    'warning',
                    check.path,
                    'ref.unknownTemplateToken',
                    `Instruction references unknown token "${check.value}"`
                );
            }
            return;
        }

        const identifiers = parseIdentifiers(check.value);
        const knownIdentifiers = new Set([
            ...Array.from(fieldIds),
            ...Array.from(contextImportIds),
        ]);
        identifiers.forEach((identifier) => {
            if (!knownIdentifiers.has(identifier)) {
                addIssue(
                    result,
                    'warning',
                    check.path,
                    'condition.unknownRef',
                    `Condition references unknown field "${identifier}"`
                );
            }
        });
    });

    result.isValidForSave = result.errors.length === 0;
    return result;
};

export const validateSchemaEditorInput = (
    rawJson: string,
    originalId: string | null
): {
    parsedRecord: Partial<DocumentSchemaRecord> | null;
    parseError?: string;
    validation: ValidationResult;
} => {
    try {
        const parsed = JSON.parse(rawJson);
        const validation = validateSchemaRecord(parsed, originalId);
        return { parsedRecord: parsed, validation };
    } catch (error: any) {
        const validation = createEmptyResult();
        addIssue(
            validation,
            'error',
            '$',
            'json.parse',
            error?.message || 'Invalid JSON'
        );
        validation.isValidForSave = false;
        return {
            parsedRecord: null,
            parseError: error?.message || 'Invalid JSON',
            validation,
        };
    }
};
